import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { DocumentAttachment } from '../models/routing-slip.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  constructor(private http: HttpClient) {}

  /**
   * View a document by opening it in a new tab or downloading it
   * @param attachment The document attachment information
   */
  viewDocument(attachment: DocumentAttachment): void {
    // For demo purposes, we'll create a mock PDF viewer
    // In a real application, you would fetch the actual document from your backend
    this.openDocumentViewer(attachment);
  }

  /**
   * Download a document
   * @param attachment The document attachment information
   */
  downloadDocument(attachment: DocumentAttachment): void {
    // For demo purposes, we'll create a mock download
    // In a real application, you would fetch the actual document from your backend
    this.createMockDownload(attachment);
  }

  /**
   * Open document in a new tab
   * @param attachment The document attachment information
   */
  openDocumentInNewTab(attachment: DocumentAttachment): void {
    // For demo purposes, we'll open the document in a new tab
    // In a real application, you would fetch the actual document from your backend
    this.openDocumentViewer(attachment);
  }

  /**
   * Open document in a viewer modal or new tab
   * @param attachment The document attachment information
   */
  private openDocumentViewer(attachment: DocumentAttachment): void {
    // For PDF files, we can use browser's built-in PDF viewer
    if (attachment.type === 'application/pdf') {
      if (attachment.fileData || attachment.blobUrl) {
        // Get the document URL
        let documentUrl: string;

        if (attachment.blobUrl) {
          // Use the existing blob URL
          documentUrl = attachment.blobUrl;
        } else if (typeof attachment.fileData === 'string') {
          // Base64 data URL or blob URL string
          documentUrl = attachment.fileData;
        } else if (attachment.fileData instanceof ArrayBuffer) {
          // ArrayBuffer data - create blob URL
          const blob = new Blob([attachment.fileData], {
            type: 'application/pdf',
          });
          documentUrl = URL.createObjectURL(blob);
        } else {
          alert(
            'Document data not available. Please upload the document again.'
          );
          return;
        }

        // Create an HTML wrapper with the document title and embedded PDF
        const htmlContent = this.createDocumentHtmlWrapper(
          attachment.name,
          documentUrl
        );
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const htmlUrl = URL.createObjectURL(htmlBlob);

        // Open the HTML wrapper in a new tab (which will have the proper title)
        const newWindow = window.open(htmlUrl, '_blank');

        // Clean up the HTML blob URL after a short delay to allow the page to load
        if (newWindow) {
          setTimeout(() => {
            URL.revokeObjectURL(htmlUrl);
          }, 1000);
        }
      } else {
        alert('Document data not available. Please upload the document again.');
      }
    } else {
      // For other file types, show a preview or download dialog
      this.showDocumentPreview(attachment);
    }
  }

  /**
   * Create an HTML wrapper for the document with proper title
   * @param documentName The name of the document (used as title)
   * @param documentUrl The URL of the document to embed
   * @returns HTML content as string
   */
  private createDocumentHtmlWrapper(
    documentName: string,
    documentUrl: string
  ): string {
    // Escape HTML special characters in the document name
    const escapedName = documentName
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapedName}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    html, body {
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  </style>
</head>
<body>
  <iframe src="${documentUrl}" type="application/pdf"></iframe>
</body>
</html>`;
  }

  /**
   * Create a mock PDF URL for demonstration
   * @param fileName The name of the file
   */
  private createMockPdfUrl(fileName: string): string {
    // This method is deprecated - we should use actual uploaded file data
    // In a real application, this would be the actual uploaded document
    return '';
  }

  /**
   * Show document preview for non-PDF files
   * @param attachment The document attachment information
   */
  private showDocumentPreview(attachment: DocumentAttachment): void {
    // For non-PDF files, we can show a preview or download dialog
    const message = `Document: ${attachment.name}\nSize: ${attachment.size}\nType: ${attachment.type}\n\nThis document would be opened in its appropriate viewer or downloaded.\n\nIn a real application, this would open the document in the appropriate application or show a preview.`;
    alert(message);
  }

  /**
   * Create a download for the document
   * @param attachment The document attachment information
   */
  private createMockDownload(attachment: DocumentAttachment): void {
    if (attachment.fileData) {
      try {
        let blob: Blob;

        if (typeof attachment.fileData === 'string') {
          // Base64 data URL
          const response = fetch(attachment.fileData);
          response
            .then((res) => res.blob())
            .then((blobData) => {
              this.downloadBlob(blobData, attachment.name);
            });
        } else if (attachment.fileData instanceof ArrayBuffer) {
          // ArrayBuffer data
          blob = new Blob([attachment.fileData], { type: attachment.type });
          this.downloadBlob(blob, attachment.name);
        }
      } catch (error) {
        alert('Error downloading file. Please try again.');
      }
    } else {
      alert(
        'Document data not available for download. Please upload the document again.'
      );
    }
  }

  /**
   * Download a blob as a file
   * @param blob The blob to download
   * @param filename The filename for the download
   */
  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Get document preview URL (for embedding in iframe or object tag)
   * @param attachment The document attachment information
   */
  getDocumentPreviewUrl(attachment: DocumentAttachment): string {
    if (attachment.type === 'application/pdf') {
      return this.createMockPdfUrl(attachment.name);
    }
    // For other file types, return a placeholder
    return '';
  }

  /**
   * Get document source for PDF viewer
   * @param attachment The document attachment information
   */
  getDocumentSource(
    attachment: DocumentAttachment
  ): string | Uint8Array | undefined {
    if (attachment.type === 'application/pdf') {
      const remoteUrl = attachment.url?.trim();
      if (remoteUrl) {
        return remoteUrl;
      }
      // If we have a blob URL, use it directly
      if (attachment.blobUrl) {
        return attachment.blobUrl;
      }

      // If we have file data, use it
      if (attachment.fileData && attachment.fileData !== null) {
        if (typeof attachment.fileData === 'string') {
          // Base64 encoded data or blob URL
          return attachment.fileData;
        } else if (attachment.fileData instanceof ArrayBuffer) {
          // ArrayBuffer data
          return new Uint8Array(attachment.fileData);
        }
      }
      // If no file data, return undefined
      return undefined;
    }
    return undefined;
  }
}
